/* oxlint-disable no-param-reassign */
import { mockMetricDataSource } from '@gonfalon/metrics/mocks/mockMetricDataSource';
import { integer } from '@gonfalon/mocks/common';
import { createMockResourceBuilder } from '@gonfalon/mocks/createMockResourceBuilder';

import type { AssignmentDataSource } from '../types';

const integrationNames: Record<string, string> = {
  'snowflake-experimentation': 'Snowflake',
  'databricks-experimentation': 'Databricks',
  'redshift-experimentation': 'Redshift',
  'bigquery-experimentation': 'BigQuery',
};

const legacyIntegrationKeyMap: Record<string, string> = {
  snowflake: 'snowflake-experimentation',
  databricks: 'databricks-experimentation',
  redshift: 'redshift-experimentation',
  bigquery: 'bigquery-experimentation',
};

function toWarehouseExperimentationIntegrationKey(integrationKey: string): string {
  return legacyIntegrationKeyMap[integrationKey] ?? integrationKey;
}

export const mockAssignmentDataSource = createMockResourceBuilder(
  (): AssignmentDataSource => {
    const base = mockMetricDataSource().create();
    const integrationKey = toWarehouseExperimentationIntegrationKey(base.integrationKey);
    const integrationName = integrationNames[integrationKey] ?? integrationKey;

    return {
      ...base,
      key: `assignment-${base.key}`,
      integrationKey,
      experimentsCount: integer({ min: 0, max: 24 }),
      experimentKeys: [],
      _integrationConfigurationSummary: {
        name: integrationName,
        id: `${integrationKey}-config`,
      },
    };
  },
  {
    withKey: (dataSourceKey: string) => (draft) => {
      draft.key = dataSourceKey;
    },
    withName: (dataSourceName: string) => (draft) => {
      draft.name = dataSourceName;
    },
    withIntegrationKey: (integrationKey: string) => (draft) => {
      const normalizedIntegrationKey = toWarehouseExperimentationIntegrationKey(integrationKey);
      draft.integrationKey = normalizedIntegrationKey;
      draft._integrationConfigurationSummary = {
        name: integrationNames[normalizedIntegrationKey] ?? normalizedIntegrationKey,
        id: `${normalizedIntegrationKey}-config`,
      };
    },
    withArchived: (archived: boolean) => (draft) => {
      draft.archived = archived;
    },
    withExperimentsCount: (experimentsCount: number) => (draft) => {
      draft.experimentsCount = experimentsCount;
    },
    withExperimentKeys: (experimentKeys: string[]) => (draft) => {
      draft.experimentKeys = experimentKeys;
    },
    withEnvironmentKey: (environmentKey: string) => (draft) => {
      draft.environmentKey = environmentKey;
      const environmentConfigs: Record<string, { name: string; color: string }> = {
        production: { name: 'Production', color: 'F5A623' },
        staging: { name: 'Staging', color: '5C7CFA' },
        development: { name: 'Development', color: '45B882' },
      };
      const envConfig = environmentConfigs[environmentKey];

      if (envConfig) {
        draft._environmentSummary = {
          name: envConfig.name,
          color: envConfig.color,
        };
      }
    },
    withLastModified: (lastModified: number) => (draft) => {
      draft._lastModified = lastModified;
    },
  },
);
